/**
 * Live console: streams on the air, most-reported first, and the kill switch
 * (design/LIVE_BROADCAST_PLAN.md Phase E, §6.3).
 *
 * Kill ends the stream at once: the room closes, the converters stop, the
 * recording is hidden and deleted by the next live sweep (within ~10 minutes),
 * and the Live reads ENDED. The host's screen says Postopia ended it. It cannot
 * be undone, so it is a two-step "click again to confirm" (no window.confirm,
 * §0c), with an optional note kept on the stream.
 */

import { PageShell } from '../components/ui/PageShell';
import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { currentEnv } from '../lib/envTarget';
import { liveApi, LiveAdminError, type LiveStreamRow } from '../lib/liveApi';

export const Route = createFileRoute('/live')({
  component: LiveRoute,
});

function LiveRoute() {
  return (
    <AdminShell mode="feature" featureName="Live">
      <LiveConsole />
    </AdminShell>
  );
}

type Filter = 'live' | 'recent';

function ago(iso: string | null): string {
  if (!iso) return '';
  const t = parseServerDate(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const END_REASON: Record<string, string> = {
  host: 'ended by the host',
  cap: 'reached 3 hours',
  dropped: 'host dropped out',
  failed: 'failed to start',
  staff: 'killed by staff',
  org_admin: 'ended by an admin',
  coverage_ended: 'coverage ended',
  revoked: 'host no longer eligible',
  deleted: 'Broadcast deleted',
};

function LiveConsole() {
  const [filter, setFilter] = useState<Filter>('live');
  const [rows, setRows] = useState<LiveStreamRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [arming, setArming] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const webOrigin = currentEnv() === 'prod' ? 'https://postopia.com' : 'https://test.postopia.com';

  const load = useCallback(async (f: Filter) => {
    setError(null);
    try {
      setRows(await liveApi.list(f));
    } catch (err) {
      if (err instanceof LiveAdminError && err.status === 403) {
        setError('Your account does not have admin access.');
      } else if (err instanceof LiveAdminError && err.code === 'live_not_configured') {
        setError('The live console is not configured on this environment yet.');
      } else {
        setError("Couldn't load streams. Try again.");
      }
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load(filter);
    // On the air changes quickly: refresh every 20 s while this view is open.
    if (filter !== 'live') return;
    const id = setInterval(() => void load('live'), 20_000);
    return () => clearInterval(id);
  }, [filter, load]);

  const kill = useCallback(
    async (row: LiveStreamRow) => {
      if (arming !== row.id) {
        setArming(row.id);
        setNote('');
        return;
      }
      setBusyId(row.id);
      setStatus(null);
      try {
        await liveApi.kill(row.id, note);
        setArming(null);
        setStatus(`Ended @${row.host_handle}'s stream. Its recording is kept 30 days as evidence (the Evidence button), then deleted.`);
        await load(filter);
      } catch (err) {
        setStatus(
          err instanceof LiveAdminError && err.code === 'not_live'
            ? 'That stream had already ended.'
            : 'Kill failed. Try again.',
        );
        await load(filter);
      } finally {
        setBusyId(null);
      }
    },
    [arming, note, filter, load],
  );

  // A killed stream's chunks are kept 30 days (0441): mint a 6-hour signed
  // link and copy it. An .m3u8 plays in Safari, VLC or any HLS player.
  const evidence = useCallback(async (row: LiveStreamRow) => {
    setBusyId(row.id);
    setStatus(null);
    try {
      const { url } = await liveApi.evidence(row.id);
      await navigator.clipboard.writeText(url);
      setStatus(`Evidence link for @${row.host_handle}'s stream copied. It works for 6 hours; open it in Safari, VLC or any HLS player.`);
    } catch (err) {
      setStatus(err instanceof LiveAdminError && err.code === 'no_evidence' ? 'No evidence is kept for that stream.' : 'Could not mint the evidence link.');
    } finally {
      setBusyId(null);
    }
  }, []);

  const card: React.CSSProperties = {
    background: 'var(--c-bg-surface)',
    border: '1px solid var(--c-border-hairline)',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  };
  const tab = (f: Filter, label: string) => (
    <button
      type="button"
      onClick={() => setFilter(f)}
      aria-pressed={filter === f}
      style={{
        padding: '7px 14px',
        borderRadius: 999,
        border: '1px solid var(--c-border-hairline)',
        background: filter === f ? 'var(--c-fg-default)' : 'transparent',
        color: filter === f ? 'var(--c-bg-page)' : 'var(--c-fg-default)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  return (
    <PageShell
      title="Live"
      description={
        <>
          Postopia Live streams, most-reported first. Killing one ends it at once for everyone,
          hides the recording and marks the Live ended. It cannot be undone.
        </>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {tab('live', 'On the air')}
        {tab('recent', 'Last 7 days')}
      </div>

      {status ? (
        <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 12px' }}>{status}</p>
      ) : null}
      {error ? <p style={{ fontSize: 14, color: '#9A2A12', margin: '0 0 12px' }}>{error}</p> : null}

      {rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>
          {filter === 'live' ? 'Nobody is live right now.' : 'No streams in the last 7 days.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => {
            const open = r.status === 'live' || r.status === 'starting';
            const isArming = arming === r.id;
            const busy = busyId === r.id;
            return (
              <div key={r.id} style={card}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        padding: '1px 8px',
                        borderRadius: 4,
                        background: open ? 'var(--c-broadcast-live, #C8322B)' : 'var(--c-bg-surface-alt)',
                        color: open ? '#FAF7F2' : 'var(--c-fg-secondary)',
                      }}
                    >
                      {r.status === 'live' ? 'LIVE' : r.status === 'starting' ? 'STARTING' : r.status === 'killed' ? 'KILLED' : 'ENDED'}
                    </span>
                    <strong style={{ fontSize: 14, color: 'var(--c-fg-default)' }}>@{r.host_handle}</strong>
                    <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>{r.host_name}</span>
                    {r.open_reports > 0 ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '1px 8px',
                          borderRadius: 999,
                          background: '#F4D9D2',
                          color: '#9A2A12',
                        }}
                      >
                        {r.open_reports} open report{r.open_reports === 1 ? '' : 's'}
                      </span>
                    ) : null}
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--c-fg-default)', margin: '6px 0 0' }}>
                    <a
                      href={`${webOrigin}/broadcast/${r.post_id}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--c-fg-link)' }}
                    >
                      {r.headline || '(untitled Live)'}
                    </a>
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--c-fg-tertiary)', margin: '6px 0 0' }}>
                    {r.status === 'live'
                      ? `started ${ago(r.started_at)} · ${r.watching ?? '?'} watching · peak ${r.peak_viewers} · ${r.guests} guest${r.guests === 1 ? '' : 's'}`
                      : `ended ${ago(r.ended_at)}${r.end_reason ? ` · ${END_REASON[r.end_reason] ?? r.end_reason}` : ''} · peak ${r.peak_viewers}`}
                    {r.slow_mode_seconds > 0 && open ? ` · slow mode ${r.slow_mode_seconds}s` : ''}
                    {r.record ? ' · recording' : ''}
                    {r.killed_by_staff ? ` · by ${r.killed_by_staff}` : ''}
                    {r.kill_note ? ` · "${r.kill_note}"` : ''}
                    {r.evidence_until ? ` · evidence kept until ${parseServerDate(r.evidence_until).toLocaleDateString()}` : ''}
                  </p>
                  {r.evidence_until ? (
                    <button
                      type="button"
                      onClick={() => void evidence(r)}
                      disabled={busy}
                      style={{
                        marginTop: 8,
                        padding: '5px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--c-border-hairline)',
                        background: 'var(--c-bg-page)',
                        color: 'var(--c-fg-default)',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Evidence link
                    </button>
                  ) : null}
                  {isArming ? (
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={500}
                      placeholder="Note for the record (optional)"
                      aria-label="Kill note"
                      style={{
                        marginTop: 10,
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--c-border-hairline)',
                        background: 'var(--c-bg-page)',
                        color: 'var(--c-fg-default)',
                        fontSize: 14,
                      }}
                    />
                  ) : null}
                </div>
                {open ? (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {isArming ? (
                      <button
                        type="button"
                        onClick={() => setArming(null)}
                        disabled={busy}
                        style={{
                          padding: '7px 14px',
                          borderRadius: 8,
                          border: '1px solid var(--c-border-hairline)',
                          background: 'transparent',
                          color: 'var(--c-fg-default)',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void kill(r)}
                      style={{
                        padding: '7px 14px',
                        borderRadius: 8,
                        border: 0,
                        background: isArming ? '#9A2A12' : '#C7563E',
                        color: '#FAF7F2',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: busy ? 'default' : 'pointer',
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {busy ? '…' : isArming ? 'Confirm: end this stream' : 'Kill stream'}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
